// src/components/AdminPartnerOrders.js
import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Button,
  Container,
  Alert,
  Spinner,
  Form,
  OverlayTrigger,
  Tooltip,
} from "react-bootstrap";
import { FaAngleUp, FaAngleDown, FaHistory } from "react-icons/fa";
import PropTypes from "prop-types";
import "toastr/build/toastr.min.css";
import toastr from "toastr";
import {
  FiMail,
  FiPhone,
  FiCalendar,
  FiMapPin,
  FiTag,
  FiFileText,
  FiEdit2,
  FiCopy,
  FiRotateCcw,
  FiDollarSign,
  FiTrash2,
  FiSquare,
  FiCheckSquare,
  FiSave,
  FiX,
} from "react-icons/fi";

import PaginationComponent from "./PaginationComponent";
import { EditOrderModal } from "./modals/ModalComponents";
import HistoryModal from "./modals/HistoryModal";
import EditPayoutSettingsModal from "./modals/EditPayoutSettingsModal";
import { formatDateTime } from "./utils";

function AdminPartnerOrders() {
  const apiUrl = process.env.REACT_APP_API_URL;
  const { uniqueId } = useParams();
  const navigate = useNavigate();

  // ===== data
  const [orders, setOrders] = useState([]);

  const [totalSum, setTotalSum] = useState(0);
  const [promoCode, setPromoCode] = useState("");
  const [dateAdded, setPartnerDateAdded] = useState("");
  const [partnerName, setPartnerName] = useState("");
  const [partnerEmail, setPartnerEmail] = useState("");
  const [partnerPhone, setPartnerPhone] = useState("");
  const [partnerNote, setPartnerNote] = useState("");
  const [partnerAddress, setPartnerAddress] = useState("");
  const [partnerBirthday, setPartnerBirthday] = useState("");
  const [payoutPercentage, setPayoutPercentage] = useState(0);
  const [totalPaidOrders, setTotalPaidOrders] = useState(0);
  const [totalUnpaidOrders, setTotalUnpaidOrders] = useState(0);
  const [partnerStatus, setStatusPartners] = useState("");
  const [paidSum, setPaidSum] = useState(0);
  const [unpaidSum, setUnpaidSum] = useState(0);

  // ===== modals
  const [editingOrder, setEditingOrder] = useState(null);
  const [showOrderModal, setShowOrderModal] = useState(false);

  // ===== history modal controls
  const [showHistoryModal, setShowHistoryModal] = useState(false);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [filteredPayments, setFilteredPayments] = useState([]);
  const [paymentsSortField, setPaymentsSortField] = useState("payment_date");
  const [paymentsSortOrder, setPaymentsSortOrder] = useState("desc");
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [filterDates, setFilterDates] = useState({ from: "", to: "" });
  const [dateInputs, setDateInputs] = useState({ from: "", to: "" });

  // ===== table controls
  const [ordersSortField, setOrdersSortField] = useState("order_date");
  const [ordersSortOrder, setOrdersSortOrder] = useState("desc");
  const [showUnpaidOnly, setShowUnpaidOnly] = useState(false);

  const [ordersPerPage, setOrdersPerPage] = useState(10);
  const [ordersCurrentPage, setOrdersCurrentPage] = useState(1);

  // ===== selection / paybar
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [selectedOrdersTotal, setSelectedOrdersTotal] = useState(0);
  const [showPaymentPanel, setShowPaymentPanel] = useState(false);

  // ===== payout settings
  const [showPayoutModal, setShowPayoutModal] = useState(false);
  const [payoutSettings, setPayoutSettings] = useState({
    STARTER: { limit: 3000, percentage: 4 },
    SILVER: { limit: 30000, percentage: 6 },
    GOLD: { limit: null, percentage: 8 },
  });

  // ===== ui
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    toastr.options = {
      closeButton: true,
      newestOnTop: true,
      progressBar: true,
      positionClass: "toast-top-right",
      timeOut: "4000",
    };
  }, []);

  useEffect(() => {
    const fetchPartner = async () => {
      setLoading(true);
      setError(null);
      try {
        const resp = await fetch(`${apiUrl}/admin/partner/${uniqueId}`, {
          credentials: "include",
        });
        if (resp.ok) {
          const data = await resp.json();

          const sorted = (data.orders || []).sort(
            (a, b) => new Date(b.order_date) - new Date(a.order_date)
          );
          setOrders(sorted);

          setTotalSum(data.total_sum || 0);
          setPromoCode(data.promo_code || "");
          setPartnerName(data.partner_name || "");
          setPartnerEmail(data.partner_email || "");
          setPartnerDateAdded(data.date_added || "");
          setPayoutPercentage(data.payout_percentage || 0);
          setTotalPaidOrders(data.total_paid_orders || 0);
          setTotalUnpaidOrders(data.total_unpaid_orders || 0);
          setStatusPartners(data.partner_status || "");
          setPaidSum(data.paid_sum || 0);
          setUnpaidSum(data.unpaid_sum || 0);
          setPartnerPhone(data.partner_phone || "");
          setPartnerNote(data.partner_note || "");
          setPartnerAddress(data.partner_address || "");
          setPartnerBirthday(data.partner_birthday || "");
          setPayoutSettings(
            data.payout_settings || {
              STARTER: { limit: 3000, percentage: 4 },
              SILVER: { limit: 30000, percentage: 6 },
              GOLD: { limit: null, percentage: 8 },
            }
          );
        } else if (resp.status === 401) {
          navigate("/login");
        } else if (resp.status === 403) {
          setError("У вас нет доступа к этому разделу");
        } else {
          setError("Ошибка при получении данных");
        }
      } catch (err) {
        setError("Ошибка соединения с сервером");
      } finally {
        setLoading(false);
      }
    };

    fetchPartner();
  }, [apiUrl, uniqueId, navigate]);

  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const resp = await fetch(
          `${apiUrl}/admin/partner/${uniqueId}/payment-history`,
          { credentials: "include" }
        );
        if (!resp.ok) {
          console.error("Не удалось получить историю выплат");
          return;
        }
        const data = await resp.json();
        const history = Array.isArray(data.history) ? data.history : [];
        const sortedHistory = [...history].sort((a, b) => {
          const dateA = new Date(a.payment_date || a.order_date || 0);
          const dateB = new Date(b.payment_date || b.order_date || 0);
          return dateB - dateA;
        });
        setPaymentHistory(sortedHistory);
        setFilteredPayments(sortedHistory);
      } catch (err) {
        console.error("Ошибка при загрузке истории выплат:", err);
      }
    };

    fetchHistory();
  }, [apiUrl, uniqueId]);

  useEffect(() => {
    const total = selectedOrders.reduce((sum, id) => {
      const o = orders.find((x) => x.id === id);
      return sum + (o ? o.payout : 0);
    }, 0);
    setSelectedOrdersTotal(total);
    setShowPaymentPanel(selectedOrders.length > 0);
  }, [selectedOrders, orders]);

  const filteredOrders = useMemo(() => {
    const base = showUnpaidOnly ? orders.filter((o) => !o.is_paid) : orders;
    return base.map((o) => ({
      ...o,
      _rowClass: o.is_paid ? "row-success" : "",
    }));
  }, [orders, showUnpaidOnly]);

  const currentOrders = useMemo(() => {
    const start = (ordersCurrentPage - 1) * ordersPerPage;
    const end = start + ordersPerPage;
    return filteredOrders.slice(start, end);
  }, [filteredOrders, ordersCurrentPage, ordersPerPage]);

  useEffect(() => {
    const totalPages = Math.max(
      1,
      Math.ceil(filteredOrders.length / ordersPerPage)
    );
    if (ordersCurrentPage > totalPages) setOrdersCurrentPage(totalPages);
  }, [filteredOrders.length, ordersPerPage, ordersCurrentPage]);

  const sortOrders = (field) => {
    const next =
      ordersSortField === field && ordersSortOrder === "asc" ? "desc" : "asc";
    setOrdersSortField(field);
    setOrdersSortOrder(next);
    setOrders((prev) =>
      [...prev].sort((a, b) => {
        const A = a?.[field] ?? "";
        const B = b?.[field] ?? "";
        if (field === "order_date" || field === "payment_date")
          return next === "desc"
            ? new Date(B) - new Date(A)
            : new Date(A) - new Date(B);
        if (typeof A === "number" && typeof B === "number")
          return next === "desc" ? B - A : A - B;
        return next === "desc"
          ? String(B).localeCompare(String(A))
          : String(A).localeCompare(String(B));
      })
    );
  };

  const sortPayments = (field) => {
    const next =
      paymentsSortField === field && paymentsSortOrder === "asc"
        ? "desc"
        : "asc";
    setPaymentsSortField(field);
    setPaymentsSortOrder(next);
    setFilteredPayments((prev) =>
      [...prev].sort((a, b) => {
        const A = a?.[field] ?? "";
        const B = b?.[field] ?? "";
        if (field === "payment_date" || field === "order_date")
          return next === "asc"
            ? new Date(A) - new Date(B)
            : new Date(B) - new Date(A);
        if (typeof A === "number" && typeof B === "number")
          return next === "asc" ? A - B : B - A;
        return next === "asc"
          ? String(A).localeCompare(String(B))
          : String(B).localeCompare(String(A));
      })
    );
  };

  const fmtEUR = (n) =>
    new Intl.NumberFormat("ru-RU", {
      style: "currency",
      currency: "EUR",
      maximumFractionDigits: 2,
    }).format(Number.isFinite(n) ? n : 0);
  const totalOrdersCount = Number(totalPaidOrders + totalUnpaidOrders) || 0;
  const averageCheck =
    totalOrdersCount > 0 ? (Number(totalSum) || 0) / totalOrdersCount : 0;
  const heroStats = [
    {
      label: "Сумма заказов",
      value: `${fmtEUR(totalSum)}`,
      hint: `Всего ${totalOrdersCount} заказов`,
    },
    {
      label: "К выплате",
      value: `${fmtEUR(unpaidSum)}`,
      hint: `Ожидает ${totalUnpaidOrders} заказов`,
    },
    {
      label: "Выплачено",
      value: `${fmtEUR(paidSum)}`,
      hint: `Оплачено ${totalPaidOrders} заказов`,
    },
    {
      label: "Средний чек",
      value: `${fmtEUR(averageCheck)}`,
      hint: "По всем заказам",
    },
  ];

  const deleteOrder = (orderId) => {
    fetch(`${apiUrl}/admin/partner/order/${orderId}/delete`, {
      method: "DELETE",
      credentials: "include",
    })
      .then((r) => {
        if (!r.ok) throw new Error();
        return r.json();
      })
      .then(() => {
        setOrders((prev) => prev.filter((o) => o.id !== orderId));
        toastr.success("Заказ удалён");
      })
      .catch(() => toastr.error("Не удалось удалить заказ"));
  };

  const markAsPaid = (orderId) => {
    const now = new Date().toISOString();
    fetch(`${apiUrl}/admin/partner/order/${orderId}/pay`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ payment_date: now }),
    })
      .then((r) => r.json())
      .then(() => {
        setOrders((prev) =>
          prev.map((o) =>
            o.id === orderId ? { ...o, is_paid: true, payment_date: now } : o
          )
        );
        setTotalPaidOrders((v) => v + 1);
        setTotalUnpaidOrders((v) => Math.max(0, v - 1));
        toastr.success("Заказ оплачен");
      })
      .catch(() => toastr.error("Не удалось оплатить заказ"));
  };

  const unmarkAsPaid = (orderId) => {
    fetch(`${apiUrl}/admin/partner/order/${orderId}/unpay`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ payment_date: null }),
    })
      .then((r) => r.json())
      .then(() => {
        setOrders((prev) =>
          prev.map((o) =>
            o.id === orderId ? { ...o, is_paid: false, payment_date: null } : o
          )
        );
        setTotalPaidOrders((v) => Math.max(0, v - 1));
        setTotalUnpaidOrders((v) => v + 1);
        toastr.success("Оплата отменена");
      })
      .catch(() => toastr.error("Не удалось отменить оплату"));
  };

  const confirmPayment = async () => {
    try {
      await Promise.all(
        selectedOrders.map((id) =>
          fetch(`${apiUrl}/admin/partner/order/${id}/pay`, {
            method: "POST",
            credentials: "include",
          })
        )
      );
      setOrders((prev) =>
        prev.map((o) =>
          selectedOrders.includes(o.id) ? { ...o, is_paid: true } : o
        )
      );
      setSelectedOrders([]);
      toastr.success("Оплата завершена");
    } catch (err) {
      toastr.error("Ошибка при оплате");
    }
  };

  const openEditModal = (order) => {
    setEditingOrder({
      ...order,
      order_date: order.order_date
        ? new Date(order.order_date).toISOString().split("T")[0]
        : "",
    });
    setShowOrderModal(true);
  };
  const closeEditModal = () => {
    setShowOrderModal(false);
    setEditingOrder(null);
  };
  const handleEditChange = (e) => {
    const { name, value } = e.target;
    if (name === "order_date") {
      const dt = new Date(value);
      if (!isNaN(dt)) {
        setEditingOrder((prev) => ({
          ...prev,
          [name]: dt.toISOString().slice(0, 16),
        }));
      }
    } else {
      setEditingOrder((prev) => ({ ...prev, [name]: value }));
    }
  };
  const saveOrder = () => {
    if (!editingOrder.order_date)
      return alert("Поле 'Дата создания сделки' не может быть пустым.");
    const payload = {
      ...editingOrder,
      order_date: new Date(editingOrder.order_date),
    };
    fetch(`${apiUrl}/admin/partner/order/${editingOrder.id}/update`, {
      method: "PUT",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    })
      .then((r) => {
        if (!r.ok) throw new Error();
        return r.json();
      })
      .then(() => {
        setOrders((prev) =>
          prev.map((o) =>
            o.id === editingOrder.id ? { ...o, ...editingOrder } : o
          )
        );
        toastr.success("Заказ обновлён");
        setShowOrderModal(false);
      })
      .catch(() => alert("Не удалось сохранить заказ."));
  };

  const savePartner = (updated) => {
    fetch(`${apiUrl}/admin/partner/${uniqueId}/update`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(updated),
    })
      .then((r) => {
        if (!r.ok) throw new Error();
        return r.json();
      })
      .then(() => {
        // локально обновим поля, которые реально пришли
        if (updated.email !== undefined) setPartnerEmail(updated.email);
        if (updated.phone !== undefined) setPartnerPhone(updated.phone);
        if (updated.address !== undefined) setPartnerAddress(updated.address);
        if (updated.note !== undefined) setPartnerNote(updated.note);
        if (updated.birthday !== undefined) setPartnerBirthday(updated.birthday);
        toastr.success("Данные партнёра сохранены");
      })
      .catch(() => toastr.error("Не удалось сохранить изменения"));
  };

  const openPayoutModal = () => setShowPayoutModal(true);
  const closePayoutModal = () => setShowPayoutModal(false);
  const savePayoutSettings = (newSettings) => {
    fetch(`${apiUrl}/admin/partner/${uniqueId}/update-payout-settings`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ payout_settings: newSettings }),
    })
      .then((r) => {
        if (!r.ok) throw new Error();
        return r.json();
      })
      .then(() => {
        toastr.success("Настройки обновлены");
        closePayoutModal();
      })
      .catch(() => toastr.error("Не удалось сохранить настройки"));
  };

  const CustomCheckbox = ({ isChecked, onToggle }) => (
    <button
      type="button"
      onClick={onToggle}
      className="check-btn"
      aria-pressed={isChecked}
      aria-label={isChecked ? "Снять выделение" : "Выбрать"}
      title={isChecked ? "Снять выделение" : "Выбрать"}
    >
      {isChecked ? <FiCheckSquare size={18} /> : <FiSquare size={18} />}
    </button>
  );
  CustomCheckbox.propTypes = {
    isChecked: PropTypes.bool.isRequired,
    onToggle: PropTypes.func.isRequired,
  };

  if (loading) {
    return (
      <div
        className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center"
        style={{ background: "rgba(255,255,255,.85)" }}
      >
        <div className="text-center">
          <Spinner animation="border" variant="secondary" />
          <p className="mt-3">Пожалуйста, подождите…</p>
        </div>
      </div>
    );
  }
  if (error) {
    return (
      <div
        className="position-fixed top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center"
        style={{ background: "rgba(255,255,255,.85)" }}
      >
        <div className="text-center">
          <Alert variant="danger">{error}</Alert>
          <Button className="mt-3" onClick={() => navigate("/dashboard")}>
            Вернуться на Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <Container fluid="xl" className="compact py-3">
      <section className="hero-board">
        <article className="hero-card hero-card--mosaic">
          <div className="hero-head">
            <div>
              <p className="eyebrow text-muted mb-1">Карточка партнёра</p>
              <h1 className="hero-heading mb-1">{partnerName || "Партнёр"}</h1>
              <div className="hero-email">{partnerEmail || "—"}</div>
              <div className="hero-date mt-2">
                <span className="hero-date__label">Дата регистрации</span>
                <span className="hero-date__value">
                  {formatDateTime(dateAdded, true) || "—"}
                </span>
              </div>
            </div>
            <div className="hero-head__status hero-head__status--admin">
              <div className="hero-badges">
                <span className="hero-pill">{partnerStatus || "STARTER"}</span>
                <span className="hero-pill hero-pill--rate">
                  {payoutPercentage ? `${payoutPercentage}%` : "—"}
                </span>
              </div>
              <Button variant="outline-secondary" className="btn-ghost btn-rounded" onClick={openPayoutModal}>
                Настройки выплат
              </Button>
            </div>
          </div>
          <div className="hero-bar">
            {heroStats.map((stat) => (
              <div className="hero-bar__item" key={stat.label}>
                <span className="hero-bar__label">{stat.label}</span>
                <span className="hero-bar__value">{stat.value}</span>
                <span className="hero-bar__hint">{stat.hint}</span>
              </div>
            ))}
          </div>
          <div className="hero-info-grid">
            <div className="hero-info-card">
              <span className="hero-info-card__label">Промокод</span>
              <span className="hero-info-card__value">{promoCode || "—"}</span>
            </div>
            <div className="hero-info-card">
              <span className="hero-info-card__label">Email</span>
              <span className="hero-info-card__value">{partnerEmail || "—"}</span>
            </div>
            <div className="hero-info-card">
              <span className="hero-info-card__label">Телефон</span>
              <span className="hero-info-card__value">{partnerPhone || "—"}</span>
            </div>
          </div>
        </article>
      </section>

      <div className="content-grid">
        <div className="content-main">
          <section className="panel panel--filters partner-orders-filters">
            <div className="panel-header">
              <div>
                <p className="eyebrow text-muted mb-1">Управление заказами</p>
                <h2 className="h5 mb-0">Фильтры и действия</h2>
              </div>
              <Button
                variant="outline-secondary"
                className="btn-ghost"
                onClick={() => setShowHistoryModal(true)}
              >
                <FaHistory className="me-2" /> История выплат
              </Button>
            </div>
            
          </section>

          <section className="panel panel--table">
            <div className="table-wrap">
              <table className="table table-pro mb-0">
                <thead>
                  <tr>
                    <th style={{ width: 44 }}>
                      <CustomCheckbox
                        isChecked={selectedOrders.length === orders.length && orders.length > 0}
                        onToggle={() =>
                          setSelectedOrders((sel) =>
                            sel.length === orders.length ? [] : orders.map((o) => o.id)
                          )
                        }
                      />
                    </th>
                    <th
                      className={`th-sortable ${ordersSortField === "order_date" ? "th-active" : ""}`}
                      onClick={() => sortOrders("order_date")}
                    >
                      Дата создания сделки{" "}
                      {ordersSortField === "order_date" &&
                        (ordersSortOrder === "asc" ? <FaAngleUp /> : <FaAngleDown />)}
                    </th>
                    <th
                      className={`th-sortable ${ordersSortField === "payment_date" ? "th-active" : ""}`}
                      onClick={() => sortOrders("payment_date")}
                    >
                      Дата оплаты{" "}
                      {ordersSortField === "payment_date" &&
                        (ordersSortOrder === "asc" ? <FaAngleUp /> : <FaAngleDown />)}
                    </th>
                    <th
                      className={`th-sortable ${ordersSortField === "partner_id" ? "th-active" : ""}`}
                      onClick={() => sortOrders("partner_id")}
                    >
                      ID клиента{" "}
                      {ordersSortField === "partner_id" &&
                        (ordersSortOrder === "asc" ? <FaAngleUp /> : <FaAngleDown />)}
                    </th>
                    <th
                      className={`th-sortable ${ordersSortField === "order_amount" ? "th-active" : ""}`}
                      onClick={() => sortOrders("order_amount")}
                    >
                      Заказ на сумму{" "}
                      {ordersSortField === "order_amount" &&
                        (ordersSortOrder === "asc" ? <FaAngleUp /> : <FaAngleDown />)}
                    </th>
                    <th
                      className={`th-sortable ${ordersSortField === "payout" ? "th-active" : ""}`}
                      onClick={() => sortOrders("payout")}
                    >
                      Сумма к выплате{" "}
                      {ordersSortField === "payout" &&
                        (ordersSortOrder === "asc" ? <FaAngleUp /> : <FaAngleDown />)}
                    </th>
                    <th style={{ width: 152 }}>Действия</th>
                  </tr>
                </thead>
                <tbody>
                  {currentOrders.map((order) => (
                    <tr key={order.id} className={order._rowClass}>
                      <td>
                        <CustomCheckbox
                          isChecked={selectedOrders.includes(order.id)}
                          onToggle={() =>
                            setSelectedOrders((sel) =>
                              sel.includes(order.id)
                                ? sel.filter((id) => id !== order.id)
                                : [...sel, order.id]
                            )
                          }
                        />
                      </td>
                      <td>{formatDateTime(order.order_date, true)}</td>
                      <td>{formatDateTime(order.payment_date, false)}</td>
                      <td>{order.partner_id}</td>
                      <td className="td-num">{fmtEUR(order.order_amount)}</td>
                      <td className="td-num">{fmtEUR(order.payout)}</td>
                      <td className="text-end">
                        {order.is_paid ? (
                          <OverlayTrigger placement="top" overlay={<Tooltip>Отменить оплату</Tooltip>}>
                            <button className="btn-icon btn-unpay me-1" onClick={() => unmarkAsPaid(order.id)}>
                              <FiRotateCcw />
                            </button>
                          </OverlayTrigger>
                        ) : (
                          <OverlayTrigger placement="top" overlay={<Tooltip>Оплатить</Tooltip>}>
                            <button className="btn-icon btn-pay me-1" onClick={() => markAsPaid(order.id)}>
                              <FiDollarSign />
                            </button>
                          </OverlayTrigger>
                        )}
                        <OverlayTrigger placement="top" overlay={<Tooltip>Редактировать</Tooltip>}>
                          <button className="btn-icon btn-edit me-1" onClick={() => openEditModal(order)}>
                            <FiEdit2 />
                          </button>
                        </OverlayTrigger>
                        <OverlayTrigger placement="top" overlay={<Tooltip>Удалить</Tooltip>}>
                          <button className="btn-icon btn-del" onClick={() => deleteOrder(order.id)}>
                            <FiTrash2 />
                          </button>
                        </OverlayTrigger>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <PaginationComponent
            currentPage={ordersCurrentPage}
            totalItems={filteredOrders.length}
            itemsPerPage={ordersPerPage}
            onPageChange={(p) => setOrdersCurrentPage(p)}
            onItemsPerPageChange={(n) => {
              setOrdersPerPage(n);
              setOrdersCurrentPage(1);
            }}
          />
        </div>

        <aside className="content-side">
          <section className="panel panel--profile">
            <PartnerInfoCard
              dateAdded={dateAdded}
              email={partnerEmail}
              phone={partnerPhone}
              birthday={partnerBirthday}
              promoCode={promoCode}
              address={partnerAddress}
              note={partnerNote}
              onSave={(payload) => savePartner(payload)}
            />
          </section>
        </aside>
      </div>

      {/* modals */}
      <EditOrderModal
        showModal={showOrderModal}
        closeEditModal={closeEditModal}
        editingOrder={editingOrder}
        handleEditChange={handleEditChange}
        saveOrder={saveOrder}
      />

      <HistoryModal
        showHistoryModal={showHistoryModal}
        closeHistoryModal={() => setShowHistoryModal(false)}
        dateInputs={dateInputs}
        setDateInputs={setDateInputs}
        setFilterDates={setFilterDates}
        filterDates={filterDates}
        setFilteredPayments={setFilteredPayments}
        paymentHistory={paymentHistory}
        currentPayments={filteredPayments.slice(
          (currentPage - 1) * itemsPerPage,
          currentPage * itemsPerPage
        )}
        sortPayments={sortPayments}
        paymentsSortField={paymentsSortField}
        paymentsSortOrder={paymentsSortOrder}
        unmarkAsPaid={unmarkAsPaid}
        currentPage={currentPage}
        setCurrentPage={setCurrentPage}
        itemsPerPage={itemsPerPage}
        setItemsPerPage={setItemsPerPage}
      />

      <EditPayoutSettingsModal
        show={showPayoutModal}
        onHide={closePayoutModal}
        payoutSettings={payoutSettings}
        saveSettings={savePayoutSettings}
      />

      {showPaymentPanel && (
        <div className="paybar">
          <span>
            <strong>Сумма к выплате:</strong> {fmtEUR(selectedOrdersTotal)}
          </span>
          <div>
            <Button variant="outline-secondary" onClick={() => setSelectedOrders([])}>
              Отмена
            </Button>
            <Button className="ms-2" variant="success" onClick={confirmPayment}>
              Оплатить
            </Button>
          </div>
        </div>
      )}
    </Container>
  );
}

/* ─── УТИЛИТЫ ─────────────────────────────────────────────────────────────── */

function toDateInputValue(v) {
  if (!v) return "";
  const d = new Date(v);
  if (isNaN(d)) return "";
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}`;
}

/* ─── ВСПОМОГАТЕЛЬНЫЕ ДЛЯ КАРТОЧКИ ───────────────────────────────────────── */

function InfoRow({ icon = null, label, value = "", copyable = false, long = false }) {
  const canCopy = copyable && value && value !== "—";
  const doCopy = async () => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
        toastr.success("Скопировано: " + value);
      }
    } catch (err) {
      console.warn("Clipboard copy failed", err);
    }
  };

  return (
    <div className={`info-row ${long ? "info-row--long" : ""}`}>
      <div className="info-icon">{icon}</div>
      <div className="info-meta">
        <div className="info-label">{label}</div>
        <div className="info-value" title={String(value || "")}>
          {value || "—"}
        </div>
      </div>
      <div className="info-actions">
        {canCopy && (
          <button
            type="button"
            className="btn-icon btn-ghost"
            onClick={doCopy}
            aria-label="Скопировать"
          >
            <FiCopy />
          </button>
        )}
      </div>
    </div>
  );
}
InfoRow.propTypes = {
  icon: PropTypes.node,
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  copyable: PropTypes.bool,
  long: PropTypes.bool,
};

/* ─── ИНЛАЙН-РЕДАКТИРУЕМАЯ КАРТОЧКА ПРОФИЛЯ ─────────────────────────────── */

function PartnerInfoCard({
  dateAdded = "",
  email = "",
  phone = "",
  birthday = "",
  promoCode = "",
  address = "",
  note = "",
  onSave,
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [form, setForm] = useState({
    email: email || "",
    phone: phone || "",
    birthday: toDateInputValue(birthday),
    address: address || "",
    note: note || "",
  });

  useEffect(() => {
    if (!isEditing) {
      setForm({
        email: email || "",
        phone: phone || "",
        birthday: toDateInputValue(birthday),
        address: address || "",
        note: note || "",
      });
    }
  }, [email, phone, birthday, address, note, isEditing]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((s) => ({ ...s, [name]: value }));
  };

  const handleCancel = () => {
    setIsEditing(false);
    setForm({
      email: email || "",
      phone: phone || "",
      birthday: toDateInputValue(birthday),
      address: address || "",
      note: note || "",
    });
  };

  const handleSave = async () => {
    const payload = {
      email: form.email,
      phone: form.phone,
      address: form.address,
      note: form.note,
      birthday: form.birthday || "",
    };
    await onSave(payload);
    setIsEditing(false);
  };

  return (
    <section className="section info-card">
      <div className="info-card__header">
        <div className="info-title">Профиль партнёра</div>
        {!isEditing ? (
          <button
            type="button"
            className="btn-icon btn-ghost"
            onClick={() => setIsEditing(true)}
            aria-label="Редактировать"
            title="Редактировать"
          >
            <FiEdit2 />
          </button>
        ) : (
          <div className="d-flex gap-1">
            <OverlayTrigger placement="top" overlay={<Tooltip>Сохранить</Tooltip>}>
              <button type="button" className="btn-icon btn-pay" onClick={handleSave} aria-label="Сохранить">
                <FiSave />
              </button>
            </OverlayTrigger>
            <OverlayTrigger placement="top" overlay={<Tooltip>Отмена</Tooltip>}>
              <button type="button" className="btn-icon btn-del" onClick={handleCancel} aria-label="Отмена">
                <FiX />
              </button>
            </OverlayTrigger>
          </div>
        )}
      </div>

      {!isEditing ? (
        <div className="info-grid">
          <InfoRow
            icon={<FiCalendar />}
            label="Дата регистрации"
            value={formatDateTime(dateAdded, true) || "—"}
          />
          <InfoRow icon={<FiMail />} label="Почта" value={email || "—"} copyable />
          <InfoRow icon={<FiPhone />} label="Телефон" value={phone || "—"} copyable />
          <InfoRow
            icon={<FiCalendar />}
            label="День рождения"
            value={formatDateTime(birthday, false) || "—"}
          />
          <InfoRow icon={<FiTag />} label="Промокод" value={promoCode || "—"} copyable />
          <InfoRow icon={<FiMapPin />} label="Адрес" value={address || "—"} long />
          <InfoRow icon={<FiFileText />} label="Комментарий" value={note || "—"} long />
        </div>
      ) : (
        <div className="info-grid">
          <div className="info-row info-row--long">
            <div className="info-icon"><FiMail /></div>
            <div className="info-meta w-100">
              <div className="info-label">Почта</div>
              <Form.Control
                size="sm"
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                placeholder="name@example.com"
              />
            </div>
          </div>

          <div className="info-row info-row--long">
            <div className="info-icon"><FiPhone /></div>
            <div className="info-meta w-100">
              <div className="info-label">Телефон</div>
              <Form.Control
                size="sm"
                type="text"
                name="phone"
                value={form.phone}
                onChange={handleChange}
                placeholder="+31 6 1234 5678"
              />
            </div>
          </div>

          <div className="info-row">
            <div className="info-icon"><FiCalendar /></div>
            <div className="info-meta w-100">
              <div className="info-label">День рождения</div>
              <Form.Control
                size="sm"
                type="date"
                name="birthday"
                value={form.birthday}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="info-row">
            <div className="info-icon"><FiTag /></div>
            <div className="info-meta w-100">
              <div className="info-label">Промокод</div>
              <Form.Control size="sm" type="text" value={promoCode || ""} disabled />
            </div>
          </div>

          <div className="info-row info-row--long">
            <div className="info-icon"><FiMapPin /></div>
            <div className="info-meta w-100">
              <div className="info-label">Адрес</div>
              <Form.Control
                size="sm"
                type="text"
                name="address"
                value={form.address}
                onChange={handleChange}
                placeholder="Улица, дом, город"
              />
            </div>
          </div>

          <div className="info-row info-row--long">
            <div className="info-icon"><FiFileText /></div>
            <div className="info-meta w-100">
              <div className="info-label">Комментарий</div>
              <Form.Control
                as="textarea"
                rows={3}
                size="sm"
                name="note"
                value={form.note}
                onChange={handleChange}
                placeholder="Примечания о партнёре"
              />
            </div>
          </div>
        </div>
      )}
    </section>
  );
}
PartnerInfoCard.propTypes = {
  dateAdded: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.instanceOf(Date),
  ]),
  email: PropTypes.string,
  phone: PropTypes.string,
  birthday: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.number,
    PropTypes.instanceOf(Date),
  ]),
  promoCode: PropTypes.string,
  address: PropTypes.string,
  note: PropTypes.string,
  onSave: PropTypes.func.isRequired,
};
export default AdminPartnerOrders;
